/**
 * ImageUploader Component
 * Reusable file picker → preview → upload → returns URL to parent.
 *
 * Props:
 *   currentUrl   {string}    - existing image URL to display (optional)
 *   folder       {string}    - upload folder: 'profiles'|'events'|'articles'|'gallery'
 *   onUpload     {function}  - called with the new URL after successful upload
 *   shape        {string}    - 'circle' | 'square' (default: 'square')
 *   label        {string}    - button label (default: 'Upload Image')
 *   previewSize  {number}    - preview px dimension (default: 120)
 */

import React, { useState, useRef, useEffect } from 'react';
import apiClient from '../api/client';

const ImageUploader = ({
  currentImageUrl = '',
  currentUrl = '',
  folder = 'general',
  onUpload,
  onImageUploaded,
  shape = 'square',
  label = 'Upload Image',
  previewSize = 120
}) => {
  // Use either currentImageUrl or currentUrl (backward compatibility)
  const initialUrl = currentImageUrl || currentUrl;
  // Use either onImageUploaded or onUpload (backward compatibility)
  const uploadCallback = onImageUploaded || onUpload;
  
  const [preview, setPreview] = useState(initialUrl || '');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Update preview when currentImageUrl prop changes
  useEffect(() => {
    if (initialUrl) {
      setPreview(initialUrl);
    }
  }, [initialUrl]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Local preview immediately
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    setError('');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);

      // apiClient doesn't set Content-Type for FormData — let browser set boundary
      const response = await apiClient.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setPreview(response.data.url);
        if (uploadCallback) {
          uploadCallback(response.data.url);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed. Please try again.');
      setPreview(initialUrl || ''); // revert preview on failure
    } finally {
      setUploading(false);
      // Reset input so same file can be re-selected after error
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const isCircle = shape === 'circle';

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>

      {/* Preview box */}
      <div
        onClick={() => !uploading && fileInputRef.current?.click()}
        style={{
          width: previewSize,
          height: previewSize,
          borderRadius: isCircle ? '50%' : '8px',
          border: '2px dashed #aaa',
          overflow: 'hidden',
          cursor: uploading ? 'not-allowed' : 'pointer',
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          flexShrink: 0
        }}
        title={uploading ? 'Uploading...' : 'Click to upload image'}
      >
        {preview ? (
          <img
            src={preview}
            alt="Preview"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <span style={{ fontSize: '12px', color: '#888', textAlign: 'center', padding: '8px' }}>
            {uploading ? '⏳' : '📷 Click to upload'}
          </span>
        )}

        {/* Uploading overlay */}
        {uploading && (
          <div style={{
            position: 'absolute', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: isCircle ? '50%' : '8px'
          }}>
            <span style={{ color: 'white', fontSize: '12px' }}>Uploading...</span>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Upload button */}
      <button
        type="button"
        onClick={() => !uploading && fileInputRef.current?.click()}
        disabled={uploading}
        style={{
          padding: '6px 14px',
          fontSize: '13px',
          backgroundColor: uploading ? '#aaa' : '#3a2410',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: uploading ? 'not-allowed' : 'pointer'
        }}
      >
        {uploading ? 'Uploading...' : label}
      </button>

      {error && (
        <p style={{ color: '#dc3545', fontSize: '12px', margin: 0, maxWidth: previewSize + 40 }}>
          {error}
        </p>
      )}
    </div>
  );
};

export default ImageUploader;
